import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text
} from '@chakra-ui/react';
import React from 'react';

import type { BillingLocationData } from '../../interface/billingLocation';
import type { SlotProps } from '../../interface/slotProps';
import {
  INK,
  INK_MUTED,
  POPOVER_BORDER,
  POPOVER_RADIUS,
  POPOVER_SHADOW,
  SURFACE_WARM
} from '../../styles/tokens';

const INTER_STACK = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

const HOVER_HINT_SX = {
  cursor: 'help',
  textDecoration: 'underline dotted var(--border-strong, #c9c8ce)',
  textUnderlineOffset: '3px',
  '&:hover, &:focus-visible': {
    textDecorationColor: 'var(--text-muted, #7a7682)'
  }
} as const;

const na = (v: unknown): string =>
  typeof v === 'string' || typeof v === 'number' ? String(v) : 'N/A';

export const BillingLocation: React.FC<SlotProps> = ({ value }) => {
  const data: BillingLocationData =
    value != null && typeof value === 'object' && !Array.isArray(value)
      ? (value as BillingLocationData)
      : {};

  return (
    <Popover trigger="hover" placement="bottom-start">
      <PopoverTrigger>
        <Text
          as="span"
          tabIndex={0}
          aria-label={`Facility: ${na(data.location)}`}
          data-testid="billing-location"
          fontFamily={INTER_STACK}
          fontSize="12px"
          fontWeight={500}
          color={INK}
          sx={HOVER_HINT_SX}
        >
          ⌂ {na(data.location)}
        </Text>
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          data-testid="billing-location-popover"
          bg={SURFACE_WARM}
          border={`1px solid ${POPOVER_BORDER}`}
          borderRadius={POPOVER_RADIUS}
          boxShadow={POPOVER_SHADOW}
          padding="11px 13px"
          width="max-content"
          minWidth="180px"
          maxWidth="280px"
        >
          <Text
            as="p"
            data-testid="billing-location-name"
            fontFamily={INTER_STACK}
            fontSize="12.5px"
            fontWeight={700}
            color={INK}
          >
            {na(data.location)}
          </Text>
          <Text
            as="p"
            data-testid="billing-location-department"
            fontFamily={INTER_STACK}
            fontSize="11px"
            fontWeight={400}
            color={INK_MUTED}
            marginTop="2px"
          >
            {na(data.department)}
          </Text>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};
